import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useQuery, useMutation } from "react-query";
import { ApplicationEventSubscription, ApplicationEventSubscriptionsService, ErrorResponse } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { useDialogState } from "hooks/useDialogState";
import { describeNotificationTemplateType } from "./applicationEventTypeUtils";
import { CreateEventSubscriptionDialog } from "./CreateEventSubscriptionDialog";
import { EditEventSubscriptionDialog } from "./EditEventSubscriptionDialog";
import { describeApplicationEventType } from "utils/applicationEventUtils";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  clientId: string;
}

const EventSubscriptionsTab: React.FC<Props> = ({ clientId }) => {
  const isReadOnly = useClientReadOnly();
  const [isCreateDialogOpen, openCreateDialog, closeCreateDialog] = useDialogState();
  const [isEditDialogOpen, openEditDialog, closeEditDialog] = useDialogState();
  const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [selectedSubscription, setSelectedSubscription] = React.useState<ApplicationEventSubscription | null>(null);

  const { data, isLoading, error, refetch } = useQuery<
    { items: ApplicationEventSubscription[]; nextToken?: string },
    ErrorResponse
  >(
    ["listEventSubscriptions", clientId],
    () => ApplicationEventSubscriptionsService.listApplicationEventSubscriptions({ clientId }),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const deleteMutation = useMutation(
    (subscriptionId: string) =>
      ApplicationEventSubscriptionsService.deleteApplicationEventSubscription({ subscriptionId }),
    {
      onSuccess: () => {
        refetch();
        closeDeleteDialog();
        setSelectedSubscription(null);
      }
    }
  );

  const handleEditClick = React.useCallback(
    (subscription: ApplicationEventSubscription) => {
      setSelectedSubscription(subscription);
      openEditDialog();
    },
    [openEditDialog]
  );

  const handleDeleteClick = React.useCallback(
    (subscription: ApplicationEventSubscription) => {
      setSelectedSubscription(subscription);
      openDeleteDialog();
    },
    [openDeleteDialog]
  );

  const handleDeleteConfirm = React.useCallback(() => {
    if (selectedSubscription) {
      deleteMutation.mutate(selectedSubscription.id);
    }
  }, [selectedSubscription, deleteMutation]);

  const handleCreateSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const handleEditSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const columns: DataTableColumn[] = [
    {
      id: "eventType",
      title: <strong>Event Type</strong>,
      cell: (row: ApplicationEventSubscription) => describeApplicationEventType(row.eventType)
    },
    {
      id: "notificationTemplateType",
      title: <strong>Notification Type</strong>,
      cell: (row: ApplicationEventSubscription) => describeNotificationTemplateType(row.notificationTemplateType)
    },
    {
      id: "emails",
      title: <strong>Notification Emails</strong>,
      cell: (row: ApplicationEventSubscription) => row.emails.join(", ")
    },
    {
      id: "notes",
      title: <strong>Notes</strong>,
      cell: (row: ApplicationEventSubscription) => row.notes || "—"
    }
  ];

  const rowActions = React.useCallback(
    (row: ApplicationEventSubscription): DataTableRowAction[] => [
      {
        type: "ACTION",
        title: "Edit",
        action: () => handleEditClick(row),
        disabled: isReadOnly
      },
      {
        type: "ACTION",
        title: "Delete",
        action: () => handleDeleteClick(row),
        color: "error.main",
        disabled: isReadOnly
      }
    ],
    [handleEditClick, handleDeleteClick, isReadOnly]
  );

  const subscriptions = React.useMemo(() => {
    return (data?.items || []).sort((a, b) => a.eventType.localeCompare(b.eventType));
  }, [data?.items]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Event Subscriptions</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} disabled={isReadOnly}>
          Add Subscription
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <ApiErrorMessage apiError={error} />
        </Box>
      )}

      <DataTable
        data={subscriptions}
        rowKey={(row: ApplicationEventSubscription) => row.id}
        columns={columns}
        rowActions={rowActions}
        isLoading={isLoading}
        dense
        noData={
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No event subscriptions found. Click "Add Subscription" to create one.
            </Typography>
          </Box>
        }
      />

      <CreateEventSubscriptionDialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        clientId={clientId}
        onSuccess={handleCreateSuccess}
      />

      {selectedSubscription && (
        <EditEventSubscriptionDialog
          open={isEditDialogOpen}
          onClose={closeEditDialog}
          subscription={selectedSubscription}
          onSuccess={handleEditSuccess}
        />
      )}

      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title="Delete Event Subscription"
          content="Are you sure you want to delete this event subscription? This action cannot be undone."
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteConfirm}
          isLoading={deleteMutation.isLoading}
          error={deleteMutation.error}
          confirmationButtonColor="error"
        />
      )}
    </Box>
  );
};

export { EventSubscriptionsTab };
